import * as React from "react"
import { Box, Typography } from "@mui/material"
import { format } from "date-fns"
import { useResponsive } from "@ui-seeker/app"
import { IconSeeker, ModalSeeker } from "@ui-seeker/core"

import {
    ANNOUNCEMENT_BRAND_SURFACE,
    ANNOUNCEMENT_CTA,
    ANNOUNCEMENT_CTA_HOVER,
    ANNOUNCEMENT_SURFACE_SHADOW,
} from "@components/AnnouncementFrame"
import { announcementColors } from "Theme/palette"

/**
 * The dialog an announcement is read in — by the popup and by the bell alike, so the same
 * announcement looks the same whichever way the user reaches it.
 *
 * The dressing is DIP Insights' own brand ramp at 135°, a frosted circle for the icon, 20px
 * corners, a two-layer shadow and a gradient CTA. An announcement is the closest thing the
 * dashboard has to a welcome, so it gets that treatment instead of the flat grey of a form
 * dialog.
 */

/**
 * The header of an announcement: frosted icon, title and date.
 *
 * Exported because the editor's preview paints the same chrome around the body. If it were
 * inlined below, the preview would be an imitation of the popup and would drift away from it
 * the first time either one is touched.
 *
 * It does NOT paint the gradient — its container does. Painting it here too left two
 * gradients stacked: the dialog's own, at full width, and this one, narrower because the
 * title leaves room for the close button. The inner one ended early and the outer one showed
 * through at a different point of the ramp, drawing a visible seam next to the X.
 */
export function AnnouncementHeader({ title, date, icon = "bullhorn" }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 3, py: 2.5 }}>
            <Box
                sx={{
                    width: 44, height: 44, flexShrink: 0, borderRadius: '50%',
                    backgroundColor: 'rgba(255,255,255,0.12)',
                    backdropFilter: 'blur(8px)',
                    border: '1px solid rgba(255,255,255,0.18)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
            >
                <IconSeeker icon={icon} sx={{ fontSize: 19, color: 'rgba(255,255,255,0.92)' }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#fff', lineHeight: 1.3 }}>
                    {title}
                </Typography>
                {date && (
                    <Typography sx={{ fontSize: 13, color: 'rgba(255,255,255,0.72)' }}>
                        {format(new Date(date), 'dd/MM/yyyy')}
                    </Typography>
                )}
            </Box>
        </Box>
    )
}

export default function AnnouncementDialog({
    isOpen,
    onClose,
    title,
    date,
    icon = "bullhorn",
    children,
    ...modalProps
}) {
    // On a phone a dialog that keeps its desktop width and its 20px corners reads as a card
    // floating in a grey void. Below the breakpoint it takes the screen instead.
    const isSmallScreen = useResponsive('down', 'sm')

    return (
        <ModalSeeker
            isOpen={isOpen}
            controller={onClose}
            // The whole header goes in as the title node, with the dialog's own padding
            // removed: the header brings its own.
            title={<Box sx={{ pr: 4 }}><AnnouncementHeader title={title} date={date} icon={icon} /></Box>}
            titleProps={{
                sx: {
                    p: 0,
                    background: ANNOUNCEMENT_BRAND_SURFACE,
                    // ModalSeeker draws the close button with a grey from the theme, which
                    // vanishes against the gradient.
                    '& button': { color: 'rgba(255,255,255,0.85)', '&:hover': { color: '#fff' } },
                },
            }}
            containerProps={{
                maxWidth: "sm",
                fullScreen: isSmallScreen,
                PaperProps: {
                    sx: {
                        borderRadius: isSmallScreen ? 0 : '20px',
                        boxShadow: ANNOUNCEMENT_SURFACE_SHADOW,
                        // Without this the gradient squares off over the paper's corners.
                        overflow: 'hidden',
                    },
                },
            }}
            // The frame brings its own inner padding; a second one from the dialog pushed the
            // text a centimetre off the edge, and the dividers drew a box inside a box.
            contentProps={{ dividers: false, sx: { px: 0, py: 0 } }}
            // A tinted footer, so the actions read as a strip and not as two buttons adrift
            // at the bottom of the text.
            actionProps={{
                sx: {
                    px: 3, py: 2,
                    backgroundColor: announcementColors.surfaceMuted,
                    borderTop: `1px solid ${announcementColors.border}`,
                },
            }}
            {...modalProps}
            cancelProps={{
                ...modalProps.cancelProps,
                sx: {
                    textTransform: 'none', borderRadius: '8px', height: 44, px: 3,
                    fontSize: 15, color: announcementColors.inkMuted,
                    borderColor: announcementColors.border,
                    '&:hover': { borderColor: '#9CA3AF', backgroundColor: '#fff' },
                    ...modalProps.cancelProps?.sx,
                },
            }}
            successProps={{
                ...modalProps.successProps,
                sx: {
                    background: ANNOUNCEMENT_CTA,
                    color: '#fff', borderRadius: '8px', height: 44, px: 3.5,
                    fontSize: 15, fontWeight: 600, textTransform: 'none',
                    '&:hover': { background: ANNOUNCEMENT_CTA_HOVER },
                    ...modalProps.successProps?.sx,
                },
            }}
        >
            {children}
        </ModalSeeker>
    )
}
